import { formatDateTime, formatINR, type Page } from "@templeos/shared";
import { Alert, Badge, Button, DataTable, DateText, Drawer, EmptyState, Money, PageHeader, Spinner, TextField, useToast, type Column } from "@templeos/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLive } from "../components/hooks.js";
import { Shell } from "../components/Shell.js";
import { ReprintModal, VoidModal, type ServerReceipt } from "../components/SupervisorActions.js";
import { api, errorText, NetworkError, qs } from "../lib/api.js";
import { db } from "../lib/db.js";
import { auditSlipJob, logPrint, printJobs, printProblem } from "../lib/print.js";
import { findLocal, shiftSales } from "../lib/queue.js";
import type { QueuedSale } from "../lib/types.js";
import { useSession } from "../session.js";

interface Row { id: string; number: string; created_at: string; total: number; status: string; kinds: string | null; modes: string | null; reprint_count: number; local?: QueuedSale }

const MODES = [{ id: "", label: "All" }, { id: "cash", label: "Cash" }, { id: "upi", label: "UPI" }] as const;
const NONE: QueuedSale[] = [];
const hhmm = (ts: string) => new Date(ts).toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });
const CODE_RE = /^[A-Z0-9]{14}$|^[A-Z0-9-]+-\d{8}-\d{5}-[A-Z0-9]{6}$/;
const PAGE = 500;      // the server's largest page
const MAX_PAGES = 40;  // 20,000 bills: far beyond any one shift

/** Every receipt of the shift that matches, page by page (a festival shift runs to thousands of bills). */
async function allReceipts(params: Record<string, string>): Promise<Row[]> {
  const rows: Row[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const p = await api.get<Page<Row>>(`/api/core/receipts${qs({ ...params, page, pageSize: PAGE })}`);
    rows.push(...p.items);
    if (rows.length >= p.total || !p.items.length) break;
  }
  return rows;
}

function localRow(x: QueuedSale): Row {
  return {
    id: x.receiptId ?? x.clientRef, number: x.number ?? `Roll #${String(x.seq).padStart(5, "0")}`, created_at: x.createdAt, total: x.total,
    status: x.voided ? "cancelled" : x.status === "rejected" ? "rejected" : x.status === "pending" ? "pending" : "issued",
    kinds: [...new Set(x.lines.map((l) => l.kind))].join(", "), modes: x.payments.map((p) => p.mode).join(", "), reprint_count: 0, local: x,
  };
}

const STATUS: Record<string, { tone: "success" | "danger" | "warning" | "neutral"; label: string }> = {
  issued: { tone: "success", label: "Issued" }, cancelled: { tone: "danger", label: "Void" }, pending: { tone: "warning", label: "Waiting to sync" },
  rejected: { tone: "danger", label: "Refused by server" },
};

/** SOW Module 4: the cashier's own current-shift transactions, search, detail, duplicate print, void and audit slip. */
export function History() {
  const s = useSession();
  const toast = useToast();
  const shiftId = s.shift?.shift.id ?? null;
  const localSales = useLive(() => (shiftId ? shiftSales(db, shiftId) : Promise.resolve(NONE)), shiftId ?? "none", NONE);
  const [mode, setMode] = useState<"" | "cash" | "upi">("");
  const [search, setSearch] = useState("");
  const [serverQ, setServerQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [detail, setDetail] = useState<{ id?: string; receipt?: ServerReceipt; local?: QueuedSale; error?: string } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const listSeq = useRef(0);   // only the newest list request may fill the table
  const detailSeq = useRef(0); // only the newest receipt request may fill the drawer
  const [reprint, setReprint] = useState<{ receiptId: string; local?: QueuedSale } | null>(null);
  const [voiding, setVoiding] = useState<{ receiptId: string; number: string; total: number } | null>(null);

  const load = useCallback(async () => {
    const seq = ++listSeq.current;
    setError(null);
    try {
      const list = await allReceipts({ shiftId: shiftId ?? "", mode, q: serverQ });
      if (seq !== listSeq.current) return;
      setRows(list);
      setOffline(false);
    } catch (e) {
      if (seq !== listSeq.current) return;
      if (e instanceof NetworkError) setOffline(true); else setError(errorText(e));
      setRows([]);
    }
  }, [shiftId, mode, serverQ]);
  useEffect(() => { void load(); }, [load]);

  const openReceipt = async (id: string, local?: QueuedSale) => {
    const seq = ++detailSeq.current;
    setDetail({ id, local });
    setDetailLoading(true);
    try {
      const receipt = await api.get<ServerReceipt>(`/api/core/receipts/${id}`);
      if (seq === detailSeq.current) setDetail({ id, receipt, local });
    } catch (e) {
      if (seq !== detailSeq.current) return;
      setDetail({ id, local, error: e instanceof NetworkError ? "The server cannot be reached, so the full receipt cannot be shown." : errorText(e) });
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  };

  const onSearch = async () => {
    const term = search.trim();
    if (CODE_RE.test(term.toUpperCase())) {
      const hit = shiftId && s.me ? await findLocal(db, term, { shiftId, cashier: s.me.username }) : undefined;
      if (hit?.receiptId) return openReceipt(hit.receiptId, hit);
      if (hit) return setDetail({ local: hit });
      toast("No bill from this terminal carries that code.", "danger");
      return;
    }
    setServerQ(term);
  };

  // Bills not on the server yet always show, on top of the server's list (or alone when offline).
  const serverIds = new Set((rows ?? []).map((r) => r.id));
  const unsynced = localSales.filter((x) => x.status !== "synced" || offline).map(localRow).filter((r) => !serverIds.has(r.id));
  const all = [...unsynced.reverse(), ...(offline ? [] : (rows ?? []).map((r) => ({ ...r, local: localSales.find((x) => x.receiptId === r.id) })))]
    .filter((r) => (!mode || (r.modes ?? "").includes(mode)) && (!from || hhmm(r.created_at) >= from) && (!to || hhmm(r.created_at) <= to));

  const printAudit = async () => {
    const label = `${MODES.find((m) => m.id === mode)?.label ?? "All"}${from || to ? ` ${from || "start"}-${to || "now"}` : ""}`;
    const printed = await printJobs(s.config?.printerUrl ?? null, [auditSlipJob(all.map((r) => ({ createdAt: r.created_at, number: r.number, total: r.total, modes: r.modes ?? "", status: r.status })), s.slip, label)]);
    const problem = printProblem(printed);
    if (!problem) logPrint("slip");
    toast(problem ? `Mini audit slip not printed: ${problem.toLowerCase()}.` : "Mini audit slip printed.", problem ? "danger" : "success");
  };

  const columns: Column<Row>[] = [
    { key: "created_at", header: "Time", sortable: true, render: (r) => hhmm(r.created_at) },
    { key: "number", header: "Receipt", sortable: true, render: (r) => <span className="tos-num">{r.number}</span> },
    { key: "kinds", header: "Items", render: (r) => r.kinds ?? "—" },
    { key: "modes", header: "Paid by", render: (r) => (r.modes ? r.modes.toUpperCase() : "Free") },
    { key: "total", header: "Amount", align: "right", sortable: true, render: (r) => <Money paise={r.total} /> },
    { key: "status", header: "Status", render: (r) => { const st = STATUS[r.status] ?? STATUS.issued!; return <Badge tone={st.tone}>{st.label}</Badge>; } },
    { key: "reprint_count", header: "Reprints", align: "center", render: (r) => r.reprint_count || "" },
  ];

  const d = detail?.receipt;
  const dl = detail?.local;
  return (
    <Shell>
      <div className="pos-page">
        <PageHeader title="My sales" subtitle={`Current shift only${s.shift ? ` · opened ${formatDateTime(s.shift.shift.opened_at)}` : ""}`}
          actions={<><Button onClick={() => void load()}>Refresh</Button><Button variant="primary" disabled={!all.length} onClick={() => void printAudit()}>Print mini audit slip</Button></>} />
        <div className="tos-toolbar">
          <div className="tos-field" style={{ minWidth: 280, flex: 1 }}>
            <label className="tos-label" htmlFor="hist-search">Barcode, receipt number, name or mobile</label>
            <input id="hist-search" className="tos-input" value={search} placeholder="Scan a ticket or type and press Enter" autoComplete="off"
              onChange={(e) => { setSearch(e.target.value); if (!e.target.value) setServerQ(""); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void onSearch(); } }} />
          </div>
          <div className="tos-field">
            <span className="tos-label" id="mode-chips">Payment</span>
            <div className="pos-chips" role="group" aria-labelledby="mode-chips">
              {MODES.map((m) => <button key={m.id} type="button" className="pos-cat" aria-pressed={mode === m.id} onClick={() => setMode(m.id)}>{m.label}</button>)}
            </div>
          </div>
          <TextField label="From" type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField label="To" type="time" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        {offline && <Alert tone="warning">Server unreachable: showing this terminal's bills for the shift.</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}
        <DataTable columns={columns} rows={rows === null ? undefined : all} loading={rows === null} rowKey={(r) => r.id} caption="Sales in the current shift"
          onRowClick={(r) => (r.local && !r.local.receiptId ? setDetail({ local: r.local }) : void openReceipt(r.id, r.local))}
          empty={<EmptyState title="No sales match">Bills from your current shift appear here.</EmptyState>} maxHeight={560} />
      </div>

      <Drawer open={Boolean(detail)} title={d ? `Receipt ${d.number}` : dl ? `Bill ${dl.number ?? dl.clientRef}` : "Receipt"} onClose={() => setDetail(null)}
        footer={d ? (
          <>
            {d.status === "issued" && s.can("counters.sell") && <Button variant="danger" onClick={() => setVoiding({ receiptId: d.id, number: d.number, total: d.total })}>Void…</Button>}
            <Button variant="primary" onClick={() => setReprint({ receiptId: d.id, local: dl })}>Reprint duplicate…</Button>
          </>
        ) : undefined}>
        {detailLoading && <Spinner />}
        {detail?.error && detail.id && (
          <Alert tone="danger">
            {detail.error}{" "}
            <Button size="sm" onClick={() => void openReceipt(detail.id ?? "", detail.local)}>Try again</Button>
          </Alert>
        )}
        {d && (
          <div className="tos-stack">
            <div className="tos-row"><Badge tone={d.status === "issued" ? "success" : "danger"}>{d.status === "issued" ? "Issued" : "Void"}</Badge>
              <DateText value={d.created_at} time />{d.reprint_count > 0 && <Badge tone="warning">{d.reprint_count} reprint(s)</Badge>}</div>
            <div className="tos-muted">Counter {d.counter_code} · {d.cashier}{d.payer_name ? ` · ${d.payer_name}` : ""}</div>
            <DataTable rowKey={(l) => String(l.line_no)} rows={d.lines} caption="Items" columns={[
              { key: "description", header: "Item" },
              { key: "qty", header: "Qty", align: "right" },
              { key: "unit_price", header: "Rate", align: "right", render: (l) => <Money paise={l.unit_price} /> },
              { key: "amount", header: "Amount", align: "right", render: (l) => <Money paise={l.amount} />, footer: <Money paise={d.total} /> },
            ]} />
            <div>{d.payments.length ? d.payments.map((p, i) => <div key={i}>{p.mode.toUpperCase()} {formatINR(p.amount)}{p.reference ? ` · ${p.reference}` : ""}</div>) : "Free pass"}</div>
            {dl?.tickets?.length ? <div className="tos-muted">Tickets: {dl.tickets.map((x) => x.code).join(", ")}</div> : null}
          </div>
        )}
        {!d && dl && (
          <div className="tos-stack">
            <Badge tone={dl.status === "rejected" ? "danger" : "warning"}>{dl.status === "rejected" ? "Refused by server" : "Waiting to sync"}</Badge>
            {dl.error && <Alert tone="danger">{dl.error.message}</Alert>}
            <div className="tos-muted">Ref {dl.clientRef} · <DateText value={dl.createdAt} time /></div>
            {dl.lines.map((l, i) => <div key={i} className="tos-row"><span>{l.qty} × {l.name}</span><span className="tos-spacer" /><Money paise={l.amount} /></div>)}
            <div className="pos-total"><span>Total</span><strong>{formatINR(dl.total)}</strong></div>
            {dl.status === "rejected" && (
              <Alert tone="warning">
                The server refused this bill for good. Take back any printed slips and return the money to the devotee. Money kept in
                the drawer shows as a cash difference at shift close, where the supervisor approves it.
              </Alert>
            )}
            {dl.status === "pending" && dl.error && <Alert tone="info">Not accepted yet: {dl.error.message} It is sent again automatically.</Alert>}
          </div>
        )}
      </Drawer>
      <ReprintModal target={reprint} onClose={() => { setReprint(null); void load(); }} />
      <VoidModal target={voiding} onClose={() => setVoiding(null)} onDone={() => { setDetail(null); void load(); void s.refreshShift(); }} />
    </Shell>
  );
}
